import { useCallback, useEffect, useState } from 'react';
import { SignInPageProps } from '@backstage/core-plugin-api';
import { useApi, microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { UserIdentity } from '@backstage/core-components';
import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Button, ButtonIcon, ButtonLink } from '@backstage/ui';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import CheckIcon from '@material-ui/icons/Check';
import ArlaLogo from '../../../public/logos/Arla_Logo_Primary_RGB.png';

const REQUEST_ACCESS_URL =
  'https://iam.arlafoods.com/ui/d/request-center/request-access';
const REQUIRED_ROLE = 'Arla Global Backstage PROD User';

type SignInState = 'loading' | 'idle' | 'signing-in' | 'error';

const useStyles = makeStyles({
  root: {
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--arla-page-content-bg)',
  },
  card: {
    padding: '40px 32px',
    maxWidth: '440px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    backgroundColor: 'var(--arla-card-bg)',
  },
  logo: {
    height: '60px',
    width: 'auto',
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    fontFamily: 'Roboto, sans-serif',
    color: 'var(--arla-text-primary-color)',
    textAlign: 'center',
    margin: 0,
  },
  signInButton: {
    height: '40px',
    padding: '0 20px !important',
    backgroundColor: '#1F5493',
    color: '#FFFFFF !important',
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'Roboto, sans-serif',
    textTransform: 'none',
    borderRadius: '4px',
    width: '100%',
    maxWidth: '280px',
    '&:hover': {
      backgroundColor: '#174077',
    },
    "&[data-disabled='true']": {
      backgroundColor: '#5b87c0',
      color: '#FFFFFF !important',
    },
  },
  divider: {
    width: '100%',
  },
  accessSection: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  accessTitle: {
    fontSize: '15px',
    fontWeight: 600,
    fontFamily: 'Roboto, sans-serif',
    color: 'var(--arla-text-primary-color)',
    textAlign: 'center',
    margin: 0,
  },
  accessDescription: {
    fontSize: '13px',
    fontFamily: 'Roboto, sans-serif',
    color: 'var(--arla-text-secondary-color)',
    textAlign: 'center',
    lineHeight: 1.5,
    margin: 0,
  },
  roleBox: {
    borderRadius: '4px',
    padding: '8px 16px',
    border: '1px solid #E5E5E5',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
  },
  roleLabel: {
    fontSize: '11px',
    fontWeight: 400,
    fontFamily: 'Roboto, sans-serif',
    color: 'var(--arla-text-secondary-color)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.6px',
    margin: 0,
  },
  roleName: {
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'Roboto, sans-serif',
    color: 'var(--arla-text-primary-color)',
    margin: 0,
  },
  roleNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  copyButton: {
    padding: '2px',
    color: 'var(--arla-text-secondary-color)',
    '&:hover': {
      color: 'var(--arla-text-primary-color)',
    },
  },
  requestButton: {
    height: '40px',
    padding: '0 20px !important',
    backgroundColor: '#08612B',
    color: '#FFFFFF !important',
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'Roboto, sans-serif',
    textTransform: 'none',
    borderRadius: '4px',
    width: '100%',
    maxWidth: '280px',
    '&:hover': {
      backgroundColor: '#064e23',
    },
  },
  retryLink: {
    fontSize: '13px',
    fontFamily: 'Roboto, sans-serif',
    color: 'var(--arla-anchor-color)',
    cursor: 'pointer',
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
    padding: 0,
  },
});

export const CustomSignInPage = ({ onSignInSuccess }: SignInPageProps) => {
  const classes = useStyles();
  const microsoftAuth = useApi(microsoftAuthApiRef);
  const [state, setState] = useState<SignInState>('loading');
  const [copied, setCopied] = useState(false);

  const signInWithResponse = useCallback(
    async (
      response: NonNullable<
        Awaited<ReturnType<typeof microsoftAuth.getBackstageIdentity>>
      >,
    ) => {
      const profile = await microsoftAuth.getProfile({ optional: false });
      onSignInSuccess(
        UserIdentity.create({
          identity: response.identity,
          authApi: microsoftAuth,
          profile,
        }),
      );
    },
    [microsoftAuth, onSignInSuccess],
  );

  // Check for an existing session on mount — no popup
  useEffect(() => {
    microsoftAuth
      .getBackstageIdentity({ optional: true })
      .then(async response => {
        if (response) {
          await signInWithResponse(response);
        } else {
          setState('idle');
        }
      })
      .catch(() => setState('idle'));
  }, [microsoftAuth, signInWithResponse]);

  const handleCopyRole = useCallback(() => {
    navigator.clipboard.writeText(REQUIRED_ROLE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  const handleSignIn = useCallback(async () => {
    setState('signing-in');
    try {
      // instantPopup: true skips the Backstage "Login Required" dialog
      // and opens the Microsoft OAuth popup directly from the user's click
      const response = await microsoftAuth.getBackstageIdentity({
        optional: false,
        instantPopup: true,
      });
      if (!response) throw new Error('No identity returned after sign-in.');
      await signInWithResponse(response);
    } catch {
      setState('error');
    }
  }, [microsoftAuth, signInWithResponse]);

  if (state === 'loading') {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Paper className={classes.card} elevation={2}>
        <img src={ArlaLogo} alt="Arla Foods" className={classes.logo} />

        <Typography className={classes.title}>Developer Platform</Typography>

        <Button
          className={classes.signInButton}
          onClick={handleSignIn}
          isDisabled={state === 'signing-in'}
        >
          {state === 'signing-in' ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            'Sign in with Microsoft'
          )}
        </Button>

        {state === 'error' && (
          <>
            <Divider className={classes.divider} />

            <div className={classes.accessSection}>
              <Typography className={classes.accessTitle}>
                Not authorized yet?
              </Typography>
              <Typography className={classes.accessDescription}>
                You are signed in to Microsoft but do not have access to
                Backstage. Request the following role in SailPoint to gain
                access:
              </Typography>

              <div className={classes.roleBox}>
                <Typography className={classes.roleLabel}>Role</Typography>
                <div className={classes.roleNameRow}>
                  <Typography className={classes.roleName}>
                    {REQUIRED_ROLE}
                  </Typography>
                  <Tooltip title={copied ? 'Copied!' : 'Copy role name'}>
                    <ButtonIcon
                      size="small"
                      variant="tertiary"
                      onClick={handleCopyRole}
                      className={classes.copyButton}
                      aria-label="Copy role name"
                      icon={
                        copied ? (
                          <CheckIcon style={{ fontSize: 14 }} />
                        ) : (
                          <FileCopyOutlinedIcon style={{ fontSize: 14 }} />
                        )
                      }
                    />
                  </Tooltip>
                </div>
              </div>

              <ButtonLink
                className={classes.requestButton}
                href={REQUEST_ACCESS_URL}
                target="_blank"
                rel="noopener noreferrer"
                iconEnd={<OpenInNewIcon style={{ fontSize: 16 }} />}
              >
                Request Access
              </ButtonLink>

              <button
                type="button"
                className={classes.retryLink}
                onClick={handleSignIn}
              >
                Already have access? Try signing in again
              </button>
            </div>
          </>
        )}
      </Paper>
    </Box>
  );
};
